import { DebugStatistiqueGranularity } from 'iadocs/interfaces/IDebug'

export type StatsPreset = 'week' | 'month' | 'year' | 'custom'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const formatIsoDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const isIsoDate = (value: string) => ISO_DATE.test(value)

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

export const parseIsoDate = (value: string) => {
  if (!isIsoDate(value)) {
    return null
  }
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return date
}

export const currentWeekRange = (today = new Date()) => {
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const weekday = monday.getDay()
  const offset = weekday === 0 ? -6 : 1 - weekday
  monday.setDate(monday.getDate() + offset)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return { from: formatIsoDate(monday), to: formatIsoDate(sunday) }
}

export const currentMonthRange = (today = new Date()) => {
  const from = new Date(today.getFullYear(), today.getMonth(), 1)
  const to = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return { from: formatIsoDate(from), to: formatIsoDate(to) }
}

export const currentYearRange = (today = new Date()) => {
  return {
    from: formatIsoDate(new Date(today.getFullYear(), 0, 1)),
    to: formatIsoDate(new Date(today.getFullYear(), 11, 31)),
  }
}

export const presetRange = (preset: Exclude<StatsPreset, 'custom'>, today = new Date()) => {
  if (preset === 'week') {
    return currentWeekRange(today)
  }
  if (preset === 'year') {
    return currentYearRange(today)
  }
  return currentMonthRange(today)
}

export const shiftPresetRange = (
  preset: Exclude<StatsPreset, 'custom'>,
  from: string,
  delta: -1 | 1
) => {
  const start = parseIsoDate(from)
  if (!start) {
    return presetRange(preset)
  }
  if (preset === 'week') {
    start.setDate(start.getDate() + delta * 7)
    return currentWeekRange(start)
  }
  if (preset === 'year') {
    return currentYearRange(new Date(start.getFullYear() + delta, 0, 1))
  }
  return currentMonthRange(new Date(start.getFullYear(), start.getMonth() + delta, 1))
}

export const isoWeekLabel = (from: string) => {
  const date = parseIsoDate(from)
  if (!date) {
    return null
  }
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = utc.getUTCDay() || 7
  utc.setUTCDate(utc.getUTCDate() + 4 - day)
  const isoYear = utc.getUTCFullYear()
  const yearStart = new Date(Date.UTC(isoYear, 0, 1))
  const week = Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  const label = `S${String(week).padStart(2, '0')}`
  if (isoYear !== date.getFullYear()) {
    return `${label} ${isoYear}`
  }
  return label
}

export const periodSuffix = (preset: StatsPreset, from: string) => {
  if (preset === 'week') {
    return isoWeekLabel(from)
  }
  if (preset === 'month') {
    const date = parseIsoDate(from)
    return date ? MONTHS[date.getMonth()] : null
  }
  return null
}

export const spanDays = (from: string, to: string) => {
  const start = Date.parse(`${from}T00:00:00`)
  const end = Date.parse(`${to}T00:00:00`)
  return Math.round((end - start) / 86400000) + 1
}

export const granularityFor = (
  preset: StatsPreset,
  from: string,
  to: string
): DebugStatistiqueGranularity => {
  if (preset === 'year') {
    return 'month'
  }
  if (preset === 'week' || preset === 'month') {
    return 'day'
  }
  const span = spanDays(from, to)
  if (span <= 62) {
    return 'day'
  }
  if (span <= 366) {
    return 'week'
  }
  return 'month'
}

export const readPreset = (value: string | null): StatsPreset => {
  if (value === 'week' || value === 'month' || value === 'year' || value === 'custom') {
    return value
  }
  return 'month'
}
